const preloadedCardImageKeys = new Set();
let cardPreloadObserver;

function cardImageCacheKey(src, srcset) {
  return `${src || ''}|${srcset?.trim() || ''}`;
}

function preloadCardImage(src, srcset) {
  if (!src) return;

  const key = cardImageCacheKey(src, srcset);
  if (preloadedCardImageKeys.has(key)) return;

  preloadedCardImageKeys.add(key);
  const img = new Image();
  if (srcset?.trim()) {
    img.srcset = srcset.trim();
  }
  img.src = src;
}

function preloadCardColourImages(root) {
  root.querySelectorAll('[data-bbs-colour-dot]').forEach((dot) => {
    preloadCardImage(dot.dataset.imageSrc, dot.dataset.imageSrcset);
    preloadCardImage(dot.dataset.hoverSrc, dot.dataset.hoverSrcset);
  });
}

function scheduleCardColourPreload(root) {
  if (root.dataset.bbsPreloadScheduled === 'true') return;
  root.dataset.bbsPreloadScheduled = 'true';

  const run = () => preloadCardColourImages(root);

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 2500 });
  } else {
    window.setTimeout(run, 300);
  }
}

function observeCardColourPreload(root) {
  if (!('IntersectionObserver' in window)) {
    scheduleCardColourPreload(root);
    return;
  }

  if (!cardPreloadObserver) {
    cardPreloadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          scheduleCardColourPreload(entry.target);
          cardPreloadObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '120px 0px', threshold: 0.01 }
    );
  }

  cardPreloadObserver.observe(root);
}

class BBSProductCard {
  static FADE_MS = 300;

  constructor(root) {
    this.root = root;
    this.media = root.querySelector('.bbs-card__media');
    this.primary = root.querySelector('.bbs-card__image--primary');
    this.hover = root.querySelector('.bbs-card__image--hover');
    this.dots = root.querySelectorAll('[data-bbs-colour-dot]');
    this.priceEl = root.querySelector('[data-bbs-card-price]');
    this.comparePriceEl = root.querySelector('[data-bbs-card-compare]');
    this.mediaLink = root.querySelector('[data-bbs-card-media-link]');
    this.titleLink = root.querySelector('[data-bbs-card-title-link]');
    this.isSwapping = false;

    this.media?.addEventListener('mouseenter', () => {
      this.root.classList.add('is-image-hovered');
    });

    this.media?.addEventListener('mouseleave', () => {
      this.root.classList.remove('is-image-hovered');
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.setColour(dot);
      });
    });

    const selectedColour = root.dataset.selectedColour;
    let matchingDot = null;
    if (selectedColour) {
      matchingDot = Array.from(this.dots).find((dot) => dot.dataset.colour === selectedColour);
    }
    if (!matchingDot) {
      matchingDot = root.querySelector('[data-bbs-colour-dot].is-active');
    }
    if (matchingDot?.dataset.imageSrc) {
      this.applyImageSources(matchingDot);
    } else if (matchingDot) {
      this.setActiveDot(matchingDot);
    }
  }

  setColour(dot) {
    if (!this.primary || !dot.dataset.imageSrc || this.isSwapping) return;
    if (dot.classList.contains('is-active')) return;

    this.isSwapping = true;
    this.root.classList.remove('is-image-hovered');
    this.primary.classList.add('is-swapping');
    if (this.hover) {
      this.hover.classList.add('is-swapping');
    }

    window.setTimeout(() => {
      this.applyImageSources(dot);

      const images = [this.primary];
      if (this.hover && dot.dataset.hoverSrc) {
        images.push(this.hover);
      }

      Promise.all(images.map((img) => this.waitForImage(img))).then(() => {
        requestAnimationFrame(() => {
          this.primary.classList.remove('is-swapping');
          if (this.hover) {
            this.hover.classList.remove('is-swapping');
          }
          this.isSwapping = false;
        });
      });
    }, BBSProductCard.FADE_MS);
  }

  applyImageSources(dot) {
    this.primary.src = dot.dataset.imageSrc;
    if (dot.dataset.imageSrcset) {
      this.primary.srcset = dot.dataset.imageSrcset.trim();
    }

    if (this.hover) {
      if (dot.dataset.hoverSrc) {
        this.hover.src = dot.dataset.hoverSrc;
        if (dot.dataset.hoverSrcset) {
          this.hover.srcset = dot.dataset.hoverSrcset.trim();
        }
        this.root.classList.add('has-hover-image');
      } else {
        this.hover.removeAttribute('srcset');
        this.hover.src = '';
        this.root.classList.remove('has-hover-image');
      }
    }

    this.root.dataset.selectedColour = dot.dataset.colour || '';
    this.setActiveDot(dot);
    this.updatePrices(dot);
    this.updateLinks(dot);
  }

  updateLinks(dot) {
    const baseUrl = this.root.dataset.productUrl || '';
    if (!baseUrl) return;

    const variantId = dot.dataset.variantId;
    const nextUrl = variantId ? `${baseUrl.split('?')[0]}?variant=${variantId}` : baseUrl.split('?')[0];

    if (this.mediaLink) {
      this.mediaLink.href = nextUrl;
    }
    if (this.titleLink) {
      this.titleLink.href = nextUrl;
    }
  }

  updatePrices(dot) {
    if (!this.priceEl || !dot.dataset.variantPrice) return;

    const formatMoney = (cents) => {
      const amount = Number(cents) || 0;
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(amount);
      }
      return `£${(amount / 100).toFixed(2)}`;
    };

    const price = Number(dot.dataset.variantPrice) || 0;
    const compareAt = Number(dot.dataset.variantCompare) || 0;
    const onSale = compareAt > price;

    this.priceEl.textContent = formatMoney(price);
    this.priceEl.classList.toggle('bbs-card__price--sale', onSale);

    if (this.comparePriceEl) {
      if (onSale) {
        this.comparePriceEl.textContent = formatMoney(compareAt);
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }
  }

  waitForImage(img) {
    if (img.complete && img.naturalWidth > 0) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = () => {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  }

  setActiveDot(dot) {
    this.dots.forEach((item) => {
      const isActive = item === dot;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }
}

function initBBSProductCards(scope = document) {
  scope.querySelectorAll('[data-bbs-product-card]').forEach((root) => {
    if (root.dataset.bbsCardInit === 'true') return;
    root.dataset.bbsCardInit = 'true';
    new BBSProductCard(root);
    observeCardColourPreload(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initBBSProductCards();
});

window.BBSProductCard = BBSProductCard;
window.BBSInitProductCards = initBBSProductCards;
